<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="filteredSummaryRecords"
      :items-per-page="-1"
      hide-default-footer
      dense
      class="pdp-summary-table"
      :item-class="() => 'table-row'"
    >
      <template v-slot:[`header.changes`]="{ header }">
        <div class="header-with-action">
          <span>{{ header.text }}</span>
          <v-btn
            v-if="selectedSummaryForActions && canEditSummary(selectedSummaryForActions) && selectedSummaryForActions.Summary_ID"
            small
            icon
            class="header-action-btn"
            @click.stop="showAddChangeDialog(selectedSummaryForActions)"
          >
            <v-icon small class="primary--text">mdi-pencil</v-icon>
          </v-btn>
        </div>
      </template>
      <template v-slot:[`header.hrSupportDetails`]="{ header }">
        <div class="header-with-action header-with-action--spread">
          <span>{{ header.text }}</span>
          <v-btn
            v-if="selectedSummaryForActions && canEditSummary(selectedSummaryForActions) && selectedSummaryForActions.Summary_ID"
            small
            icon
            class="header-action-btn"
            @click.stop="showAddHrSupportDetailDialog(selectedSummaryForActions)"
          >
            <v-icon small class="primary--text">mdi-plus</v-icon>
          </v-btn>
        </div>
      </template>
      <template v-slot:[`item.salary`]="{ item }">
        <div class="salary-cell">
          <div v-if="item.Summary_ID" class="salary-content">
            <div class="salary-grid">
              <div class="salary-metric">
                <div class="salary-metric-label">Prev</div>
                <div class="salary-metric-value">
                  <span class="salary-currency">$</span>
                  <span v-if="isSalaryVisible(item)" class="salary-number">{{ formatMoney(getDisplayPreviousSalary(item)) }}</span>
                  <span v-else class="salary-number salary-number--masked">••••</span>
                </div>
              </div>
              <div class="salary-metric">
                <div class="salary-metric-label">New</div>
                <div class="salary-metric-value">
                  <span class="salary-currency">$</span>
                  <span v-if="isSalaryVisible(item)" class="salary-number">{{ formatMoney(item.New_Salary || 0) }}</span>
                  <span v-else class="salary-number salary-number--masked">••••</span>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="salary-content">
            <div class="salary-empty grey--text">No summary</div>
          </div>
          <div class="salary-actions">
            <v-btn
              v-if="item.Summary_ID"
              icon
              small
              class="salary-toggle-btn"
              @click="toggleSalaryVisibility(item)"
            >
              <v-icon class="action-eye-icon">{{ isSalaryVisible(item) ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
            </v-btn>
            <v-btn
              v-if="canEditSummary(item) && item.Summary_ID"
              icon
              small
              class="ml-2 salary-edit-btn"
              @click="editSummary(item)"
            >
              <v-icon class="action-pencil-icon">mdi-pencil</v-icon>
            </v-btn>
            <v-btn
              v-if="canEditSummary(item) && !item.Summary_ID"
              small
              icon
              class="ml-2"
              @click="addSummaryForMeeting(item)"
            >
              <v-icon small class="primary--text">mdi-plus</v-icon>
            </v-btn>
          </div>
        </div>
      </template>

      <template v-slot:[`item.changes`]="{ item }">
        <div class="changes-cell">
          <div class="changes-content">
            <div v-if="item.changes && item.changes.length > 0" class="changes-list">
              <div
                v-for="change in item.changes"
                :key="change.Change_ID"
                class="change-item"
              >
                <div class="change-date">{{ getChangeDateFormatted(change) }}</div>
                <div class="change-row">
                  <div class="change-text">
                    <span class="change-type">{{ change.Change_Type }}:</span>
                    <span>{{ change.From_Value }} → {{ change.To_Value }}</span>
                  </div>
                  <v-btn
                    v-if="canEditSummary(item)"
                    icon
                    x-small
                    class="delete-icon-btn"
                    @click="removeChange(item, change)"
                  >
                    <v-icon class="action-delete-icon">mdi-delete-outline</v-icon>
                  </v-btn>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template v-slot:[`item.notes`]="{ item }">
        <div class="notes-cell">
          <div class="notes-content">
            <div
              v-if="getCurrentSummaryNote(item) && addingNoteSummaryId !== item.Summary_ID"
              class="notes-list"
            >
              <span
                v-if="isNotesVisible(item)"
                :key="getCurrentSummaryNote(item).Note_ID || item.Summary_ID"
                class="note-comment"
                v-html="formatNoteHtml(getCurrentSummaryNote(item).Note_Text)"
              ></span>
              <span
                v-else
                :key="`masked-${getCurrentSummaryNote(item).Note_ID || item.Summary_ID}`"
                class="note-comment note-comment--masked"
              >{{ maskNoteText(getCurrentSummaryNote(item).Note_Text || '-') }}</span>
            </div>
            <v-textarea
              v-if="canEditSummary(item) && addingNoteSummaryId === item.Summary_ID"
              :ref="'noteInput-' + item.Summary_ID"
              :value="summaryNoteDrafts[item.Summary_ID] || ''"
              placeholder="Note"
              rows="1"
              auto-grow
              dense
              flat
              solo
              hide-details
              class="notes-inline-input"
              @input="setSummaryNoteDraft(item.Summary_ID, $event)"
              @blur="onSummaryNoteBlur(item)"
              @keydown.enter.exact.prevent="onSummaryNoteBlur(item)"
            />
            <div
              v-else-if="!getCurrentSummaryNote(item) && addingNoteSummaryId !== item.Summary_ID"
              class="no-notes"
            >
              -
            </div>
          </div>
          <div class="notes-actions">
            <v-btn
              v-if="item.Summary_ID && getCurrentSummaryNote(item) && addingNoteSummaryId !== item.Summary_ID"
              small
              icon
              class="notes-toggle-btn"
              @click="toggleNotesVisibility(item)"
            >
              <v-icon class="action-eye-icon">{{ isNotesVisible(item) ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
            </v-btn>
            <v-btn
              v-if="canEditSummary(item) && addingNoteSummaryId !== item.Summary_ID"
              small
              icon
              class="add-note-btn"
              @click="showNewNoteField(item)"
            >
              <v-icon class="action-pencil-icon">mdi-pencil</v-icon>
            </v-btn>
          </div>
        </div>
      </template>

      <template v-slot:[`item.hrSupport`]="{ item }">
        <div class="hr-support-cell">
          <v-select
            v-if="canEditSummary(item)"
            v-model="item.HR_Support"
            :items="hrSupportOptions"
            append-icon=""
            dense
            flat
            hide-details
            placeholder="HR"
            class="hr-support-select"
            @change="updateSummary(item)"
          >
            <template v-slot:item="{ item }">
              <v-chip
                :color="item === 'Yes' ? 'yellow' : 'grey'"
                small
                text-color="black"
              >
                {{ item }}
              </v-chip>
            </template>
            <template v-slot:selection="{ item }">
              <div :class="['dropdown-pill', getHrSupportPillClass(item)]">
                <span class="dropdown-pill__text">{{ item || 'No' }}</span>
                <v-icon small class="dropdown-pill__icon">mdi-menu-down</v-icon>
              </div>
            </template>
          </v-select>
          <v-select
            v-else
            :value="item.HR_Support || 'No'"
            :items="[item.HR_Support || 'No']"
            :color="item.HR_Support === 'Yes' ? 'yellow' : 'grey'"
            append-icon=""
            dense
            flat
            hide-details
            placeholder="HR"
            disabled
            small
            text-color="black"
            class="hr-support-select"
          >
            <template v-slot:selection="{ item: selected }">
              <div :class="['dropdown-pill', getHrSupportPillClass(selected || item.HR_Support || 'No')]">
                <span class="dropdown-pill__text">{{ selected || item.HR_Support || 'No' }}</span>
                <v-icon small class="dropdown-pill__icon">mdi-menu-down</v-icon>
              </div>
            </template>
          </v-select>
        </div>
      </template>

      <template v-slot:[`item.hrSupportDetails`]="{ item }">
        <div class="hr-support-details-cell">
          <div class="hr-support-details-content">
            <div v-if="item.hrSupportDetails && item.hrSupportDetails.length > 0" class="hr-details-list">
              <div
                v-for="detail in item.hrSupportDetails"
                :key="detail.Detail_ID"
                class="hr-detail-card"
              >
                <div class="hr-detail-card-content">
                  <div class="hr-detail-header">
                    <div class="hr-detail-header-left">
                      <v-chip
                        :color="detail.Status === 'Solved' ? 'green' : 'red'"
                        small
                        class="hr-status-chip"
                        text-color="white"
                      >
                        {{ detail.Status }}
                      </v-chip>
                      <v-menu
                        v-if="detail.Reminder_Type && canEditSummary(item)"
                        offset-y
                        bottom
                        left
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <button
                            type="button"
                            class="hr-detail-reminder-trigger"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon small class="hr-detail-reminder-bell">mdi-bell</v-icon>
                            <span class="hr-detail-reminder-label">{{ formatHrReminderLabel(detail) }}</span>
                          </button>
                        </template>
                        <v-list dense>
                          <v-list-item @click="setReminder(item, detail, 'Weekly')">
                            <v-list-item-title>Remind Weekly</v-list-item-title>
                          </v-list-item>
                          <v-list-item @click="setReminder(item, detail, 'Monthly')">
                            <v-list-item-title>Remind Monthly</v-list-item-title>
                          </v-list-item>
                          <v-list-item @click="clearReminder(item, detail)">
                            <v-list-item-title>Clear Reminder</v-list-item-title>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                      <div
                        v-else-if="detail.Reminder_Type"
                        class="hr-detail-reminder-readonly"
                      >
                        <v-icon small class="hr-detail-reminder-bell">mdi-bell</v-icon>
                        <span class="hr-detail-reminder-label">{{ formatHrReminderLabel(detail) }}</span>
                      </div>
                    </div>
                    <div class="hr-detail-actions">
                      <v-btn
                        v-if="canEditSummary(item)"
                        icon
                        x-small
                        class="hr-action-btn primary--text"
                        @click="editHrSupportDetail(item, detail)"
                      >
                        <v-icon class="action-pencil-icon">mdi-pencil</v-icon>
                      </v-btn>
                      <v-btn
                        v-if="canEditSummary(item)"
                        icon
                        x-small
                        class="hr-action-btn delete-icon-btn"
                        @click="deleteHrSupportDetail(item, detail)"
                      >
                        <v-icon class="action-delete-icon">mdi-delete-outline</v-icon>
                      </v-btn>
                    </div>
                  </div>
                  <div
                    v-if="detail.Due_Date || detail.Note_Text"
                    class="hr-detail-stack"
                  >
                    <div
                      v-if="detail.Due_Date"
                      class="hr-detail-due"
                    >
                      <v-icon x-small class="hr-detail-due-icon">mdi-calendar</v-icon>
                      <span>Due {{ formatDate(detail.Due_Date) }}</span>
                    </div>
                    <div
                      v-if="detail.Note_Text"
                      class="hr-detail-note"
                    >
                      {{ detail.Note_Text }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </v-data-table>

    <!-- Add Change Dialog -->
    <v-dialog v-model="showChangeDialog" max-width="600">
      <v-card>
        <v-card-title>Add Change</v-card-title>
        <v-card-text>
          <v-select
            v-model="newChange.Change_Type"
            :items="changeTypes"
            label="Change Type"
            solo
            dense
            @change="onChangeTypeChange"
          ></v-select>
          <v-select
            v-model="newChange.From_Value"
            :items="fromOptions"
            label="From"
            solo
            dense
          ></v-select>
          <v-select
            v-model="newChange.To_Value"
            :items="toOptions"
            label="To"
            solo
            dense
          ></v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showChangeDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="addChange">Add</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- HR Support Detail Dialog -->
    <v-dialog v-model="showHrSupportDialog" max-width="600">
      <v-card>
        <v-card-title>{{ editingHrSupportDetail ? 'Edit HR Support Detail' : 'Add HR Support Detail' }}</v-card-title>
        <v-card-text>
          <v-select
            v-model="hrSupportDetailForm.status"
            :items="['Solved', 'Unsolved']"
            label="Status"
            solo
            dense
          ></v-select>
          <v-textarea
            v-model="hrSupportDetailForm.noteText"
            label="Note"
            rows="3"
            solo
            dense
          ></v-textarea>
          <v-menu
            v-model="dueDateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="hrSupportDetailForm.dueDate"
                label="Due Date"
                prepend-icon="mdi-calendar"
                readonly
                solo
                dense
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="hrSupportDetailForm.dueDate"
              @input="dueDateMenu = false"
            ></v-date-picker>
          </v-menu>
          <v-select
            v-model="hrSupportDetailForm.reminderType"
            :items="reminderTypes"
            label="Reminder Type"
            solo
            dense
          >
            <template v-slot:item="{ item }">
              <v-list-item-title>{{ item === 'Remind Weekly' ? 'Weekly' : item === 'Remind Monthly' ? 'Monthly' : item }}</v-list-item-title>
            </template>
            <template v-slot:selection="{ item }">
              {{ item === 'Remind Weekly' ? 'Weekly' : item === 'Remind Monthly' ? 'Monthly' : item }}
            </template>
          </v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeHrSupportDialog">Cancel</v-btn>
          <v-btn color="primary" @click="saveHrSupportDetail">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Summary Dialog -->
    <v-dialog v-model="showSummaryDialog" max-width="600">
      <v-card>
        <v-card-title>{{ editingSummary ? 'Edit Salary' : 'Add Salary' }}</v-card-title>
        <v-card-text>
          <v-text-field
            v-model.number="summaryForm.previousSalary"
            label="Previous Salary"
            type="number"
            solo
            dense
          ></v-text-field>
          <v-text-field
            v-model.number="summaryForm.newSalary"
            label="New Salary"
            type="number"
            solo
            dense
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeSummaryDialog">Cancel</v-btn>
          <v-btn color="primary" @click="saveSummary">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete HR Support Detail Dialog -->
    <ConfirmOrDeniedDialog
      v-if="showDeleteHrSupportDialog"
      v-model="showDeleteHrSupportDialog"
      :mode="confirmOrDeniedDialogModes.delete"
      message="Are you sure you want to delete this HR support detail?"
      @onDeleteClick="confirmDeleteHrSupportDetail"
      @onCancelClick="showDeleteHrSupportDialog = false"
      @onCloseClick="showDeleteHrSupportDialog = false"
    />

    <ConfirmOrDeniedDialog
      v-if="showDeleteSummaryDialog"
      v-model="showDeleteSummaryDialog"
      :mode="confirmOrDeniedDialogModes.delete"
      message="Are you sure you want to delete this summary record?"
      @onDeleteClick="confirmDeleteSummary"
      @onCancelClick="cancelDeleteSummary"
      @onCloseClick="cancelDeleteSummary"
    />
  </div>
</template>

<script>
import { getApiErrorMessage } from '@/utils/apiError'
import { formatMeansText } from '@/utils/linkifyText'
import { formatLocalDate } from '@/utils/dateUtils'
export default {
  name: 'PdpSummaryTab',
  components: {
    ConfirmOrDeniedDialog: () => import('@/components/global/ConfirmOrDeniedDialog.vue')
  },
  props: {
    employee: {
      required: true,
      type: Object
    },
    rolesOfUser: {
      required: false,
      type: Array,
      default: () => []
    },
    selectedMeeting: {
      required: false,
      type: Object,
      default: null
    }
  },
  data () {
    return {
      headers: [
        { text: 'Salary (Rate)', value: 'salary', sortable: false, width: '120px', align: 'left' },
        { text: 'Changes', value: 'changes', sortable: false, width: '300px', align: 'left' },
        { text: 'HR Support', value: 'hrSupport', sortable: false, width: '110px', align: 'center' },
        { text: 'HR Support Details', value: 'hrSupportDetails', sortable: false, width: '420px', align: 'left' },
        { text: 'Notes', value: 'notes', sortable: false, align: 'left' }
      ],
      summaryRecords: [],
      showChangeDialog: false,
      showHrSupportDialog: false,
      showSummaryDialog: false,
      showDeleteHrSupportDialog: false,
      showDeleteSummaryDialog: false,
      summaryRecordToDelete: null,
      deleteHrSupportDetailData: null,
      dueDateMenu: false,
      currentSummary: null,
      editingSummary: null,
      editingHrSupportDetail: null,
      addingNoteSummaryId: null,
      summaryNoteDrafts: {},
      noteSaveLocks: {},
      salaryVisibility: {},
      notesVisibility: {},
      newChange: {
        Change_Type: '',
        From_Value: '',
        To_Value: ''
      },
      hrSupportDetailForm: {
        status: 'Unsolved',
        noteText: '',
        dueDate: null,
        reminderType: null
      },
      summaryForm: {
        previousSalary: 0,
        newSalary: 0
      },
      changeTypes: ['Position Transfer', 'Unit', 'Supervisor'],
      hrSupportOptions: ['No', 'Yes'],
      reminderTypes: ['Remind Weekly', 'Remind Monthly'],
      fromOptions: [],
      toOptions: []
    }
  },
  computed: {
    user () {
      return this.$store.getters.getUser
    },
    isHRorFinancierOrManager () {
      return this.rolesOfUser.some(role => role.RoleName === 'HR' || role.RoleName === 'financier' || role.RoleName === 'manager')
    },
    isCurrentEmployee () {
      return this.employee && this.user && this.employee.Employee_ID === this.user.employee_id
    },
    employeeCurrentSalary () {
      if (!this.employee?.Employee_Rate?.length) return 0
      const rate = this.employee.Employee_Rate[this.employee.Employee_Rate.length - 1]
      return rate.Salary_Type === 'RATE' ? (rate.Rate ?? 0) : (rate.Salary ?? 0)
    },
    confirmOrDeniedDialogModes () {
      return this.$store.getters.getConfirmOrDeniedDialogModes
    },
    filteredSummaryRecords () {
      if (!this.selectedMeeting || !this.selectedMeeting.Meeting_ID) {
        return this.summaryRecords
      }
      const selectedMeetingId = String(this.selectedMeeting.Meeting_ID)
      return this.summaryRecords.filter(record => {
        const meetingId = record.Meeting_ID_From_Meeting || record.Meeting_ID
        return String(meetingId) === selectedMeetingId
      })
    },
    selectedSummaryForActions () {
      return this.filteredSummaryRecords.length ? this.filteredSummaryRecords[0] : null
    }
  },
  async mounted () {
    if (this.employee && this.employee.Employee_ID) {
      await this.loadEmployeeRate()
      await this.loadSummaryRecords()
    }
  },
  methods: {
    emitPdpPeriodMetadataChanged () {
      this.$emit('pdp-period-metadata-changed')
    },
    formatNoteHtml (value) {
      return formatMeansText(value)
    },
    canEditSummary (summary) {
      if (!summary || !summary.Date_Time) return false
      return this.isHRorFinancierOrManager
    },
    async loadEmployeeRate () {
      const res = await this.axios.get(`/api/employee_rate/${this.employee.Employee_ID}`, {
        headers: { Authorization: 'Bearer ' + this.user.token }
      })
      const rate = Array.isArray(res.data) ? res.data : (res.data ? [res.data] : [])
      this.$store.dispatch('setEmployeeRate', { rate, employeeId: this.employee.Employee_ID })
    },
    getDisplayPreviousSalary (item) {
      if (!item) return 0
      return item.Previous_Salary ?? 0
    },
    async loadSummaryRecords () {
      try {
        const response = await this.axios.get(`/api/pdp/summary/${this.employee.Employee_ID}`, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        this.summaryRecords = response.data

        for (const record of this.summaryRecords) {
          if (record.Summary_ID) {
            await this.loadSummaryData(record)
          }
        }
      } catch (error) {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      }
    },
    getPreviousPeriodNewSalary (currentMeetingDate) {
      if (!currentMeetingDate || !this.summaryRecords.length) return null
      const current = new Date(currentMeetingDate).getTime()
      const withSummary = this.summaryRecords
        .filter(r => r.Summary_ID && r.New_Salary != null && new Date(r.Date_Time).getTime() < current)
      if (!withSummary.length) return null
      const previous = [...withSummary].sort((a, b) => new Date(b.Date_Time).getTime() - new Date(a.Date_Time).getTime())[0]
      return previous.New_Salary
    },
    addSummaryForMeeting (meeting) {
      this.currentSummary = {
        Meeting_ID: meeting.Meeting_ID_From_Meeting || meeting.Meeting_ID,
        Employee_ID: this.employee.Employee_ID,
        Previous_Salary: 0,
        New_Salary: 0,
        HR_Support: 'No'
      }
      this.editingSummary = null
      const previousPeriodNew = this.getPreviousPeriodNewSalary(meeting.Date_Time)
      this.summaryForm = {
        previousSalary: previousPeriodNew != null ? previousPeriodNew : this.employeeCurrentSalary,
        newSalary: 0
      }
      this.showSummaryDialog = true
    },
    async loadSummaryData (record) {
      try {
        const [changesRes, notesRes, hrSupportRes] = await Promise.all([
          this.axios.get(`/api/pdp/summary/${record.Summary_ID}/changes`, {
            headers: { Authorization: 'Bearer ' + this.user.token }
          }),
          this.axios.get(`/api/pdp/summary/${record.Summary_ID}/notes`, {
            headers: { Authorization: 'Bearer ' + this.user.token }
          }),
          this.axios.get(`/api/pdp/summary/${record.Summary_ID}/hr-support`, {
            headers: { Authorization: 'Bearer ' + this.user.token }
          })
        ])

        this.$set(record, 'changes', changesRes.data)
        this.$set(record, 'notes', notesRes.data || [])
        this.$set(record, 'hrSupportDetails', hrSupportRes.data)
      } catch (error) {
        this.$set(record, 'changes', [])
        this.$set(record, 'notes', [])
        this.$set(record, 'hrSupportDetails', [])
      }
    },
    formatDate (dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
    },
    formatHrReminderLabel (detail) {
      const t = detail && detail.Reminder_Type
      if (!t) return ''
      if (t === 'Weekly' || t === 'Remind Weekly') return 'Weekly'
      if (t === 'Monthly' || t === 'Remind Monthly') return 'Monthly'
      return t
    },
    formatMoney (value) {
      const number = Number(value || 0)
      const rounded = Math.round(number)
      return rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
    },
    isSalaryVisible (item) {
      if (!item || !item.Summary_ID) return false
      return !!this.salaryVisibility[item.Summary_ID]
    },
    isNotesVisible (item) {
      if (!item || !item.Summary_ID) return false
      return !!this.notesVisibility[item.Summary_ID]
    },
    toggleSalaryVisibility (item) {
      if (!item || !item.Summary_ID) return
      this.$set(this.salaryVisibility, item.Summary_ID, !this.salaryVisibility[item.Summary_ID])
    },
    toggleNotesVisibility (item) {
      if (!item || !item.Summary_ID) return
      this.$set(this.notesVisibility, item.Summary_ID, !this.notesVisibility[item.Summary_ID])
    },
    maskNoteText (text) {
      if (!text) return ''
      return String(text).replace(/\S/g, '*')
    },
    getChangeDate (change) {
      if (!change) return null
      const raw = change.Created_At ?? change.created_at ?? change.CREATED_AT ?? null
      if (raw != null) {
        const d = new Date(raw)
        if (!isNaN(d.getTime())) return raw
      }
      for (const key of Object.keys(change || {})) {
        if (/created|date|time/i.test(key)) {
          const val = change[key]
          if (val != null && !isNaN(new Date(val).getTime())) return val
        }
      }
      return null
    },
    getChangeDateFormatted (change) {
      const raw = this.getChangeDate(change)
      return raw ? this.formatDate(raw) : '—'
    },
    getHrSupportPillClass (value) {
      return value === 'Yes' ? 'dropdown-pill--support-yes' : 'dropdown-pill--support-no'
    },
    async updateSummary (record) {
      try {
        if (!record.Summary_ID) {
          await this.axios.post('/api/pdp/summary', {
            meetingId: record.Meeting_ID_From_Meeting || record.Meeting_ID,
            employeeId: this.employee.Employee_ID,
            previousSalary: 0,
            newSalary: 0,
            hrSupport: record.HR_Support || 'No'
          }, {
            headers: {
              Authorization: 'Bearer ' + this.user.token
            }
          })
          await this.loadSummaryRecords()
          this.$store.dispatch('setSuccess', { successMsg: 'HR Support updated successfully' })
        } else {
          await this.axios.put(`/api/pdp/summary/${record.Summary_ID}`, {
            previousSalary: record.Previous_Salary || 0,
            newSalary: record.New_Salary || 0,
            hrSupport: record.HR_Support
          }, {
            headers: {
              Authorization: 'Bearer ' + this.user.token
            }
          })
          await this.loadSummaryRecords()
          this.$store.dispatch('setSuccess', { successMsg: 'HR Support updated successfully' })
        }
        this.emitPdpPeriodMetadataChanged()
      } catch (error) {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      }
    },
    showAddChangeDialog (record) {
      this.currentSummary = record
      this.newChange = { Change_Type: '', From_Value: '', To_Value: '' }
      this.fromOptions = []
      this.toOptions = []
      this.showChangeDialog = true
    },
    async onChangeTypeChange () {
      if (!this.newChange.Change_Type) {
        this.fromOptions = []
        this.toOptions = []
        return
      }

      try {
        if (this.newChange.Change_Type === 'Position Transfer') {
          const statusResponse = await this.axios.get('/api/employee_status', {
            headers: {
              Authorization: 'Bearer ' + this.user.token
            }
          })
          const positions = statusResponse.data || []

          const currentPosition = this.employee.Status || ''
          this.fromOptions = currentPosition ? [currentPosition] : []

          this.toOptions = positions
            .filter(pos => pos.Status_Descr && pos.Status_Descr !== currentPosition)
            .map(pos => pos.Status_Descr)
        } else if (this.newChange.Change_Type === 'Supervisor') {
          const employeesResponse = await this.axios.get('/api/employeelist', {
            headers: {
              Authorization: 'Bearer ' + this.user.token
            }
          })
          const employees = employeesResponse.data || []

          const currentSupervisorId = this.employee.Supervisor_ID
          const currentSupervisorName = this.employee.Supervisor_Full_Name
          let currentSupervisor = null

          if (currentSupervisorId) {
            currentSupervisor = employees.find(emp => emp.employee_id === currentSupervisorId)
          }

          if (!currentSupervisor && currentSupervisorName && currentSupervisorName !== '[ ]') {
            currentSupervisor = employees.find(emp => emp.full_name === currentSupervisorName)
          }

          this.fromOptions = currentSupervisor ? [currentSupervisor.full_name] : (currentSupervisorName && currentSupervisorName !== '[ ]' ? [currentSupervisorName] : [])

          this.toOptions = employees
            .filter(emp => emp.employee_id !== currentSupervisorId)
            .map(emp => emp.full_name)
        } else if (this.newChange.Change_Type === 'Unit') {
          const deptResponse = await this.axios.get('/api/department', {
            headers: {
              Authorization: 'Bearer ' + this.user.token
            }
          })
          const departments = deptResponse.data || []

          const currentDepartment = this.employee.Department || ''
          this.fromOptions = currentDepartment ? [currentDepartment] : []

          this.toOptions = departments
            .filter(dept => dept.Dep_Name && dept.Dep_Name !== currentDepartment && dept.Is_Active)
            .map(dept => dept.Dep_Name)
        }
      } catch (error) {
        console.error('Error loading options:', error)
        this.fromOptions = []
        this.toOptions = []
      }
    },
    async addChange () {
      if (!this.newChange.Change_Type || !this.newChange.From_Value || !this.newChange.To_Value) return

      try {
        await this.axios.post(`/api/pdp/summary/${this.currentSummary.Summary_ID}/changes`, {
          changeType: this.newChange.Change_Type,
          fromValue: this.newChange.From_Value,
          toValue: this.newChange.To_Value
        }, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        await this.loadSummaryData(this.currentSummary)
        this.showChangeDialog = false
        this.$store.dispatch('setSuccess', { successMsg: 'Change added successfully' })
        this.emitPdpPeriodMetadataChanged()
      } catch (error) {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      }
    },
    async removeChange (record, change) {
      try {
        await this.axios.delete(`/api/pdp/summary/changes/${change.Change_ID}`, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        await this.loadSummaryData(record)
        this.$store.dispatch('setSuccess', { successMsg: 'Change removed successfully' })
        this.emitPdpPeriodMetadataChanged()
      } catch (error) {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      }
    },
    setSummaryNoteDraft (summaryId, value) {
      this.$set(this.summaryNoteDrafts, summaryId, value)
    },
    showNewNoteField (record) {
      if (!record || !record.Summary_ID) return
      if (this.addingNoteSummaryId === record.Summary_ID) {
        this.$nextTick(() => {
          const ref = this.$refs['noteInput-' + record.Summary_ID]
          const comp = Array.isArray(ref) ? ref[0] : ref
          if (comp && comp.$el) {
            const textarea = comp.$el.querySelector('textarea')
            if (textarea) textarea.focus()
          }
        })
        return
      }
      this.currentSummary = record
      this.addingNoteSummaryId = record.Summary_ID
      const currentNote = this.getCurrentSummaryNote(record)
      this.$set(this.summaryNoteDrafts, record.Summary_ID, currentNote ? (currentNote.Note_Text || '') : '')
      this.$nextTick(() => {
        const ref = this.$refs['noteInput-' + record.Summary_ID]
        const comp = Array.isArray(ref) ? ref[0] : ref
        if (comp && comp.$el) {
          const textarea = comp.$el.querySelector('textarea')
          if (textarea) textarea.focus()
        }
      })
    },
    async onSummaryNoteBlur (record) {
      if (!record || !record.Summary_ID) return
      const summaryId = record.Summary_ID
      if (this.noteSaveLocks[summaryId]) return
      this.$set(this.noteSaveLocks, summaryId, true)
      try {
        const draft = this.summaryNoteDrafts[record.Summary_ID]
        const text = typeof draft === 'string' ? draft : ''
        const currentNote = this.getCurrentSummaryNote(record)
        const currentText = ((currentNote && currentNote.Note_Text) || '')
        this.addingNoteSummaryId = null
        if (text === currentText) return
        await this.saveSummaryNote(record, text)
      } finally {
        this.$set(this.noteSaveLocks, summaryId, false)
      }
    },
    async saveSummaryNote (record, text) {
      if (!record || !record.Summary_ID) return

      try {
        await this.axios.post(`/api/pdp/summary/${record.Summary_ID}/notes`, {
          noteText: text
        }, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        await this.loadSummaryData(record)
        this.$store.dispatch('setSuccess', { successMsg: 'Note saved successfully' })
        this.emitPdpPeriodMetadataChanged()
      } catch (error) {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      }
    },
    getCurrentSummaryNote (record) {
      if (!record || !Array.isArray(record.notes) || !record.notes.length) return null
      return record.notes[0]
    },
    showAddHrSupportDetailDialog (record) {
      this.currentSummary = record
      this.hrSupportDetailForm = {
        status: 'Unsolved',
        noteText: '',
        dueDate: null,
        reminderType: null
      }
      this.showHrSupportDialog = true
    },
    editHrSupportDetail (record, detail) {
      this.currentSummary = record
      this.editingHrSupportDetail = detail.Detail_ID
      this.hrSupportDetailForm = {
        status: detail.Status,
        noteText: detail.Note_Text || '',
        dueDate: detail.Due_Date ? formatLocalDate(detail.Due_Date) : null,
        reminderType: detail.Reminder_Type ? (detail.Reminder_Type === 'Weekly' ? 'Remind Weekly' : detail.Reminder_Type === 'Monthly' ? 'Remind Monthly' : detail.Reminder_Type) : null
      }
      this.showHrSupportDialog = true
    },
    deleteHrSupportDetail (record, detail) {
      this.deleteHrSupportDetailData = { record, detail }
      this.showDeleteHrSupportDialog = true
    },
    async confirmDeleteHrSupportDetail () {
      if (!this.deleteHrSupportDetailData) return
      const { record, detail } = this.deleteHrSupportDetailData
      try {
        await this.axios.delete(`/api/pdp/summary/hr-support/${detail.Detail_ID}`, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        await this.loadSummaryData(record)
        this.$store.dispatch('setSuccess', { successMsg: 'HR support detail deleted successfully' })
        this.deleteHrSupportDetailData = null
        this.emitPdpPeriodMetadataChanged()
      } catch (error) {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      }
    },
    closeHrSupportDialog () {
      this.showHrSupportDialog = false
      this.editingHrSupportDetail = null
      this.hrSupportDetailForm = {
        status: 'Unsolved',
        noteText: '',
        dueDate: null,
        reminderType: null
      }
    },
    async saveHrSupportDetail () {
      try {
        const reminderType = this.hrSupportDetailForm.status === 'Solved'
          ? null
          : (this.hrSupportDetailForm.reminderType === 'Remind Weekly' ? 'Weekly' : this.hrSupportDetailForm.reminderType === 'Remind Monthly' ? 'Monthly' : this.hrSupportDetailForm.reminderType)
        if (this.editingHrSupportDetail) {
          await this.axios.put(`/api/pdp/summary/hr-support/${this.editingHrSupportDetail}`, {
            status: this.hrSupportDetailForm.status,
            noteText: this.hrSupportDetailForm.noteText,
            dueDate: this.hrSupportDetailForm.dueDate,
            reminderType: reminderType
          }, {
            headers: {
              Authorization: 'Bearer ' + this.user.token
            }
          })
          this.$store.dispatch('setSuccess', { successMsg: 'HR support detail updated successfully' })
        } else {
          await this.axios.post(`/api/pdp/summary/${this.currentSummary.Summary_ID}/hr-support`, {
            status: this.hrSupportDetailForm.status,
            noteText: this.hrSupportDetailForm.noteText,
            dueDate: this.hrSupportDetailForm.dueDate,
            reminderType: reminderType
          }, {
            headers: {
              Authorization: 'Bearer ' + this.user.token
            }
          })
          this.$store.dispatch('setSuccess', { successMsg: 'HR support detail added successfully' })
        }
        await this.loadSummaryData(this.currentSummary)
        this.closeHrSupportDialog()
        this.emitPdpPeriodMetadataChanged()
      } catch (error) {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      }
    },
    async setReminder (record, detail, reminderType) {
      try {
        await this.axios.put(`/api/pdp/summary/hr-support/${detail.Detail_ID}`, {
          status: detail.Status,
          noteText: detail.Note_Text || '',
          dueDate: detail.Due_Date || null,
          reminderType: reminderType
        }, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        await this.loadSummaryData(record)
        this.$store.dispatch('setSuccess', { successMsg: `Reminder set to ${reminderType}` })
        this.emitPdpPeriodMetadataChanged()
      } catch (error) {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      }
    },
    async clearReminder (record, detail) {
      try {
        await this.axios.put(`/api/pdp/summary/hr-support/${detail.Detail_ID}`, {
          status: detail.Status,
          noteText: detail.Note_Text || '',
          dueDate: detail.Due_Date || null,
          reminderType: null
        }, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        await this.loadSummaryData(record)
        this.$store.dispatch('setSuccess', { successMsg: 'Reminder cleared successfully' })
        this.emitPdpPeriodMetadataChanged()
      } catch (error) {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      }
    },
    editSummary (record) {
      this.currentSummary = record
      this.editingSummary = record
      this.summaryForm = {
        previousSalary: record.Previous_Salary || 0,
        newSalary: record.New_Salary || 0
      }
      this.showSummaryDialog = true
    },
    closeSummaryDialog () {
      this.showSummaryDialog = false
      this.currentSummary = null
      this.editingSummary = null
      this.summaryForm = {
        previousSalary: 0,
        newSalary: 0,
        hrSupport: 'No'
      }
    },
    async saveSummary () {
      try {
        if (this.editingSummary) {
          await this.axios.put(`/api/pdp/summary/${this.editingSummary.Summary_ID}`, {
            previousSalary: this.summaryForm.previousSalary,
            newSalary: this.summaryForm.newSalary,
            hrSupport: this.editingSummary.HR_Support || 'No'
          }, {
            headers: {
              Authorization: 'Bearer ' + this.user.token
            }
          })
          this.$store.dispatch('setSuccess', { successMsg: 'Salary updated successfully' })
        } else {
          await this.axios.post('/api/pdp/summary', {
            meetingId: this.currentSummary.Meeting_ID,
            employeeId: this.currentSummary.Employee_ID,
            previousSalary: this.summaryForm.previousSalary,
            newSalary: this.summaryForm.newSalary,
            hrSupport: this.currentSummary.HR_Support || 'No'
          }, {
            headers: {
              Authorization: 'Bearer ' + this.user.token
            }
          })
          this.$store.dispatch('setSuccess', { successMsg: 'Salary added successfully' })
        }
        await this.loadSummaryRecords()
        this.closeSummaryDialog()
        this.emitPdpPeriodMetadataChanged()
      } catch (error) {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      }
    },
    deleteSummary (record) {
      this.summaryRecordToDelete = record
      this.showDeleteSummaryDialog = true
    },
    cancelDeleteSummary () {
      this.showDeleteSummaryDialog = false
      this.summaryRecordToDelete = null
    },
    async confirmDeleteSummary () {
      const record = this.summaryRecordToDelete
      this.showDeleteSummaryDialog = false
      this.summaryRecordToDelete = null

      if (!record) return
      try {
        await this.axios.delete(`/api/pdp/summary/${record.Summary_ID}`, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        await this.loadSummaryRecords()
        this.$store.dispatch('setSuccess', { successMsg: 'Summary deleted successfully' })
        this.emitPdpPeriodMetadataChanged()
      } catch (error) {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      }
    }
  }
}
</script>

<style scoped>
.pdp-summary-table {
  background: var(--v-surface-base);
  border-radius: 4px;
}

.header-with-action {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.header-with-action--spread {
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  box-sizing: border-box;
}

.header-action-btn {
  width: 24px;
  height: 24px;
  min-width: 24px;
}

.action-pencil-icon {
  color: #1976d2 !important;
  font-size: 18px !important;
  opacity: 1 !important;
}

.action-delete-icon {
  color: #e53935 !important;
  font-size: 18px !important;
}

.action-eye-icon {
  color: rgba(0, 0, 0, 0.54) !important;
  font-size: 18px !important;
}

.theme--dark .action-eye-icon {
  color: rgba(255, 255, 255, 0.7) !important;
}

.salary-toggle-btn,
.notes-toggle-btn {
  margin-right: 2px;
}

.salary-number--masked {
  letter-spacing: 2px;
}

.note-comment--masked {
  letter-spacing: 1px;
  user-select: none;
  opacity: 0.6;
  font-family: monospace;
}

.notes-actions {
  display: flex;
  align-items: center;
  gap: 0;
  margin-left: 8px;
}

.pdp-summary-table >>> .v-data-table__wrapper {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
}

.theme--dark .pdp-summary-table >>> .v-data-table__wrapper {
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.pdp-summary-table >>> table {
  border-collapse: collapse;
}

.pdp-summary-table >>> table tbody td {
  padding: 4px !important;
  border-right: 1px solid rgba(0, 0, 0, 0.08) !important;
}

.pdp-summary-table >>> table tbody td:has(.hr-support-cell) {
  padding: 0 !important;
}

.theme--dark .pdp-summary-table >>> table tbody td {
  border-right: 1px solid rgba(255, 255, 255, 0.08) !important;
  background-color: var(--v-surface-base) !important;
  color: rgba(255, 255, 255, 0.87) !important;
}

.pdp-summary-table >>> table tbody td:last-child {
  border-right: none !important;
}

.pdp-summary-table >>> .v-input__append-inner {
  padding: 0 !important;
}

.pdp-summary-table >>> table thead th {
  border-right: 1px solid rgba(0, 0, 0, 0.12) !important;
  white-space: nowrap !important;
}

.theme--dark .pdp-summary-table >>> table thead th {
  border-right: 1px solid rgba(255, 255, 255, 0.12) !important;
  background-color: rgba(255, 255, 255, 0.06) !important;
  color: rgba(255, 255, 255, 0.87) !important;
}

.pdp-summary-table >>> table thead th:last-child {
  border-right: none !important;
}

.table-row {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.theme--dark .table-row {
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.table-row:hover {
  background-color: rgba(var(--v-theme-on-surface-rgb, 0, 0, 0), 0.02);
}

.theme--dark .table-row:hover {
  background-color: rgba(var(--v-theme-on-surface-rgb, 255, 255, 255), 0.08);
}

.date-time-cell {
  display: flex;
  align-items: center;
  padding: 8px 0;
}

.date-time-content {
  display: flex;
  flex-direction: column;
}

.status-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(var(--v-theme-on-surface-rgb, 0, 0, 0), 0.6);
  margin-bottom: 4px;
}

.theme--dark .status-label {
  color: rgba(var(--v-theme-on-surface-rgb, 255, 255, 255), 0.7);
}

.date-text {
  font-size: 0.875rem;
  color: var(--v-theme-on-surface);
}

.action-icons {
  display: flex;
  align-items: center;
}

.action-icon {
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.2s;
}

.action-icon:hover {
  opacity: 1;
}

.salary-cell {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.salary-content {
  flex: 1;
}

.salary-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 0;
}

.salary-edit-btn {
  margin-top: 0;
}

.salary-cell {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 0;
}

.salary-content {
  flex: 1;
}

.salary-actions {
  display: flex;
  align-items: center;
  justify-content: center;
}

.salary-grid {
  display: flex;
  gap: 20px;
  align-items: flex-start;
  margin-bottom: 4px;
}

.salary-metric {
  min-width: 60px;
}

.salary-metric-label {
  font-size: 10px;
  line-height: 1.1;
  color: #6f8fb8;
  font-weight: 700;
  margin-bottom: 2px;
}

.salary-metric-value {
  font-size: 16px;
  line-height: 1.15;
  font-weight: 700;
  color: #2f3e4f;
  display: inline-flex;
  align-items: baseline;
}

.theme--dark .salary-metric-label {
  color: rgba(144, 202, 249, 0.85);
}

.theme--dark .salary-metric-value {
  color: rgba(255, 255, 255, 0.87);
}

.salary-currency {
  margin-right: 4px;
}

.salary-number {
  letter-spacing: 0.2px;
}

.salary-empty {
  font-size: 15px;
  padding-top: 10px;
}

.hr-support-cell {
  display: flex;
  justify-content: center;
  padding: 8px 0;
}

.hr-support-cell .hr-support-select {
  width: fit-content !important;
  min-width: 0;
}

.hr-support-select >>> .v-input__slot,
.hr-support-select >>> .v-select__selections {
  display: flex !important;
  justify-content: center !important;
}

/* Hide empty v-select input so chip stays centered */
.hr-support-select >>> .v-select__selections input {
  width: 0 !important;
  min-width: 0 !important;
  flex: 0 0 0 !important;
  padding: 0 !important;
  overflow: hidden !important;
}

.changes-cell {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 2px 0;
  text-align: left;
}

.changes-content {
  flex: 1;
  min-width: 0;
  text-align: left;
}

.add-change-btn {
  align-self: center;
  margin-left: 8px;
}

.hr-support-details-cell {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 4px 0;
}

.hr-support-details-content {
  flex: 1;
  min-width: 0;
}

.notes-cell {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0;
}

.notes-content {
  flex: 1;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
}

.add-note-btn {
  margin-left: 8px;
}

.notes-inline-input {
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

.notes-inline-input >>> .v-input {
  width: 100% !important;
  max-width: 100% !important;
  min-width: 0 !important;
}

.notes-inline-input >>> .v-input__control {
  min-height: 24px !important;
  width: 100% !important;
  max-width: 100% !important;
  min-width: 0 !important;
}

.notes-inline-input >>> .v-input__slot {
  margin-bottom: 0 !important;
  min-height: 24px !important;
  padding: 0 2px !important;
  border: none !important;
  box-shadow: none !important;
  background: transparent !important;
  width: 100% !important;
  max-width: 100% !important;
  min-width: 0 !important;
}

.notes-inline-input >>> .v-input__slot::before,
.notes-inline-input >>> .v-input__slot::after {
  display: none !important;
}

.notes-inline-input >>> textarea {
  font-size: 0.875rem !important;
  line-height: 1.35 !important;
  padding: 2px 0 !important;
  white-space: pre-line !important;
  word-break: break-word !important;
  overflow-wrap: anywhere !important;
  overflow-x: hidden !important;
  max-width: 100% !important;
}

.theme--dark .notes-inline-input >>> textarea {
  color: rgba(255, 255, 255, 0.87) !important;
}

.changes-list {
  display: flex;
  flex-direction: column;
  gap: 0;
  margin-bottom: 2px;
  min-width: 0;
  width: 100%;
}

.change-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.theme--dark .change-item {
  border-bottom-color: rgba(255, 255, 255, 0.12);
}

.change-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.change-date {
  font-size: 12px;
  color: #6f7781;
  line-height: 1.1;
  margin-bottom: 4px;
  text-align: left;
}

.theme--dark .change-date {
  color: rgba(255, 255, 255, 0.55);
}

.period-cell {
  padding: 4px 0;
}

.period-cell .period-text {
  font-size: 0.875rem;
}

.change-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  min-height: 24px;
}

.change-text {
  color: #1f252d;
  font-size: 14px;
  line-height: 1.2;
  word-break: break-word;
  flex: 1;
  text-align: left;
}

.theme--dark .change-text {
  color: rgba(255, 255, 255, 0.87);
}

.change-type {
  font-weight: 700;
  margin-right: 4px;
}

.delete-icon-btn {
  flex-shrink: 0;
  width: 24px !important;
  height: 24px !important;
  min-width: 24px !important;
  margin: 0 !important;
}

.delete-icon-btn .v-icon {
  color: #e53935 !important;
}

.notes-group {
  margin-bottom: 8px;
  padding-bottom: 8px;
}

.notes-group:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.notes-category {
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(var(--v-theme-on-surface-rgb, 0, 0, 0), 0.6);
  margin-bottom: 4px;
}

.theme--dark .notes-category {
  color: rgba(var(--v-theme-on-surface-rgb, 255, 255, 255), 0.7);
}

.notes-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  min-width: 0;
  width: 100%;
}

.note-comment {
  display: inline-flex;
  align-items: flex-start;
  gap: 4px;
  font-size: 0.875rem;
  color: var(--v-theme-on-surface);
  padding: 2px 0;
  margin-right: 8px;
  margin-bottom: 4px;
  max-width: 100%;
  min-width: 0;
  white-space: pre-line;
  word-break: break-word;
}

.note-comment >>> a {
  color: #1976d2;
  text-decoration: underline;
  word-break: break-all;
}

.note-comment__text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.note-comment--long {
  cursor: pointer;
}

.note-comment--long:hover {
  opacity: 0.85;
}

.note-comment__delete-btn {
  margin-left: 2px !important;
}

.full-note-text {
  white-space: pre-wrap;
  word-break: break-word;
}

.hr-support-select {
  font-size: 0.875rem;
}

.hr-support-select >>> .v-input__control {
  min-height: 24px !important;
  height: 24px !important;
}

.hr-support-select >>> .v-input__slot {
  margin-bottom: 0 !important;
  padding: 0 2px !important;
  min-height: 24px !important;
  background: transparent !important;
  box-shadow: none !important;
  border: none !important;
}

.hr-support-select >>> .v-input__slot::before,
.hr-support-select >>> .v-input__slot::after {
  display: none !important;
}

.hr-support-select >>> .v-select__selections {
  padding-left: 0;
}

.hr-support-select >>> .v-select__selections .v-select__selection--comma {
  padding-left: 0;
}

.hr-support-select >>> .v-input__append-inner {
  display: none !important;
}

.hr-support-select >>> .v-select__selection {
  width: 100%;
  max-width: 100%;
  margin-right: 0 !important;
}

.dropdown-pill {
  min-height: 28px;
  padding: 0 8px 0 10px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  max-width: 100%;
}

.dropdown-pill__text {
  color: #1f1f1f;
  font-size: 12px;
  line-height: 1;
  white-space: nowrap;
}

.dropdown-pill__icon {
  color: #555 !important;
  font-size: 16px !important;
  flex-shrink: 0;
}

.dropdown-pill--support-no {
  background: #bdbdbd;
}

.dropdown-pill--support-yes {
  background: #f5c542;
}

.hr-details-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.hr-detail-card {
  padding: 8px 0;
  margin-bottom: 4px;
}

.hr-detail-card:last-child {
  margin-bottom: 0;
  border-bottom: none;
  padding-bottom: 0;
}

.theme--dark .hr-detail-card:last-child {
  border-bottom: none;
}

.hr-detail-card-content {
  padding: 0;
}

.hr-detail-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 0;
}

.hr-detail-header-left {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1;
}

.hr-detail-reminder-trigger,
.hr-detail-reminder-readonly {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  padding: 0;
  background: none;
  border: none;
  font: inherit;
  line-height: 1.2;
}

.hr-detail-reminder-trigger {
  cursor: pointer;
  border-radius: 4px;
}

.hr-detail-reminder-trigger:hover {
  opacity: 0.85;
}

.hr-detail-reminder-trigger:focus {
  outline: none;
}

.theme--light .hr-detail-reminder-trigger:focus-visible {
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.35);
}

.hr-detail-reminder-readonly {
  cursor: default;
}

.hr-detail-reminder-bell {
  color: var(--v-primary-base) !important;
}

.hr-detail-reminder-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--v-primary-base);
}

.hr-detail-stack {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 10px;
}

.hr-detail-due {
  font-size: 0.75rem;
  color: rgba(var(--v-theme-on-surface-rgb, 0, 0, 0), 0.6);
  display: flex;
  align-items: center;
  gap: 4px;
}

.hr-detail-due-icon {
  color: rgba(var(--v-theme-on-surface-rgb, 0, 0, 0), 0.55) !important;
  flex-shrink: 0;
}

.theme--dark .hr-detail-due {
  color: rgba(var(--v-theme-on-surface-rgb, 255, 255, 255), 0.7);
}

.theme--dark .hr-detail-due-icon {
  color: rgba(var(--v-theme-on-surface-rgb, 255, 255, 255), 0.7) !important;
}

.hr-status-chip {
  font-weight: 500;
  height: 24px;
  min-width: 70px;
  justify-content: center;
}

.hr-detail-actions {
  display: flex;
  gap: 4px;
  align-items: center;
}

.hr-action-btn {
  width: 24px;
  height: 24px;
  min-width: 24px;
  padding: 0;
}

.hr-action-btn:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.theme--dark .hr-action-btn:hover {
  background-color: rgba(255, 255, 255, 0.08);
}

.hr-detail-note {
  font-size: 0.875rem;
  color: var(--v-theme-on-surface);
  line-height: 1.5;
  word-wrap: break-word;
  text-align: left;
  white-space: pre-wrap;
}

</style>
